import { API_CONFIG } from "@/api/config";
import { Sheet, useSetSheet, useSheet, useUpdateSheet } from "@/domain/Sheet";
import { SheetFieldSchema } from "@/domain/SheetFieldSchema";
import { auth } from "@/firebase";
import { computeObjectHash } from "@/utils/computeObjectHash";
import { cyrb53, xor53 } from "@/utils/cyrb53";
import { generateId } from "@/utils/generateId";
import { createLogger } from "@/utils/logger";
import { genNanoidStream } from "@/utils/nanoidStream";
import { onAuthStateChanged } from "firebase/auth";
import { nanoid } from "nanoid";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  MinimalTypeSchemasToRawSheetRowData,
  SheetRowsIO,
  SheetRowsIOTransactionJob,
} from "./core";
import { SheetRowsIOQuery } from "./query";
import { ldsIndexedDBCache } from "./wsldsCache";

const WSLDS_LOG_ENABLED = !!localStorage.getItem("__WSLDS_LOG_ENABLED__");
const logger = createLogger("WSLDS", WSLDS_LOG_ENABLED);
enum WSLDSReadyState {
  CONNECTING = 0,
  OPEN = 1,
  CLOSING = 2,
  CLOSED = 3,
}

type WSLDSSheetDataRow = {
  id: string;
  value: {
    [key: string]: string;
  };
};
type WSLDSSubCommandInsert = {
  subcommandType: "insert";
  rows: WSLDSSheetDataRow[];
  afterRowId?: string; // 挿入先; この row_id を持つ行の直後に挿入する; 省略時は先頭に挿入する
};
type WSLDSSubCommandUpdate = {
  subcommandType: "update";
  rows: WSLDSSheetDataRow[]; // 更新する行と更新内容; TODO 更新前データもつけてあげる
};
type WSLDSSubCommandDelete = {
  subcommandType: "delete";
  rowIds: string[]; // 削除対象となる行のID
};
type WSLDSSubCommandMove = {
  subcommandType: "move";
  rowIds: string[]; // 移動対象となる行のID
  afterRowId?: string; // 移動先; この row_id を持つ行の直後に挿入する; 省略時は先頭に挿入する
};
type WSLDSSubCommand =
  | WSLDSSubCommandInsert
  | WSLDSSubCommandUpdate
  | WSLDSSubCommandDelete
  | WSLDSSubCommandMove;

type WSLDSQueryOperator =
  | "=="
  | "!="
  | ">"
  | "<"
  | ">="
  | "<="
  | "in"
  | "not-in";
type WSLDSQueryDataType = "number" | "string";

// クエリは `[対象セルの値] [演算子] [targetValue]`となる.
// Firestore の where と同じ書き味になるはず
type WSLDSQuery = {
  dataType: WSLDSQueryDataType; // 数値として比較 or 文字列として比較
  operator: WSLDSQueryOperator;
  schema: string; // クエリ対象の列(sheet_field_schemas)のID
  targetValue: string | string[]; // 比較対象の値
};

const getFilterRowsByQueriesFn = (queries: WSLDSQuery[]) => {
  const collator = new Intl.Collator("ja");

  return (row: { id: string; value: { [key: string]: string } }) => {
    for (const query of queries) {
      const cellValue = row.value[query.schema];
      if (query.operator === "==") {
        if (cellValue !== query.targetValue) return false;
      } else if (query.operator === "!=") {
        if (cellValue === query.targetValue) return false;
      } else if (query.operator === ">") {
        if (query.dataType === "number") {
          if (Number(cellValue) <= Number(query.targetValue)) return false;
        } else {
          if (collator.compare(cellValue, query.targetValue as string) <= 0)
            return false;
        }
      } else if (query.operator === "<") {
        if (query.dataType === "number") {
          if (Number(cellValue) >= Number(query.targetValue)) return false;
        } else {
          if (collator.compare(cellValue, query.targetValue as string) >= 0)
            return false;
        }
      } else if (query.operator === ">=") {
        if (query.dataType === "number") {
          if (Number(cellValue) < Number(query.targetValue)) return false;
        } else {
          if (collator.compare(cellValue, query.targetValue as string) < 0)
            return false;
        }
      } else if (query.operator === "<=") {
        if (query.dataType === "number") {
          if (Number(cellValue) > Number(query.targetValue)) return false;
        } else {
          if (collator.compare(cellValue, query.targetValue as string) > 0)
            return false;
        }
      } else if (query.operator === "in") {
        if (!query.targetValue.includes(cellValue)) return false;
      } else if (query.operator === "not-in") {
        if (query.targetValue.includes(cellValue)) return false;
      }
    }
    return true;
  };
};

type WSLDSMessageSheetIdentifiers = {
  // シートを指定するための要素
  orgId: string;
  versionId: string;
  sheetId: string;
};
type WSLDSMessageCommon = {
  // すべてのメッセージタイプに共通する要素
  clientMessageId: string; // クライアント内においてのみ一意なメッセージID; クライアントが自前で採番する
  publishedAt: number; // メッセージの発行時刻
};
type WSLDSServerMessage =
  | {
      messageType: "expires";
      expiresAt: number;
    }
  | ({
      messageType: "allData";
      listenerId: string;
      cacheHash?: string;
      rows?: WSLDSSheetDataRow[];
    } & WSLDSMessageSheetIdentifiers)
  | ({
      messageType: "useCache";
      listenerId: string;
    } & WSLDSMessageSheetIdentifiers)
  | ({
      clientMessageId: string;
      messageType: "write";
      subcommands: WSLDSSubCommand[];
    } & WSLDSMessageSheetIdentifiers)
  | {
      messageType: "accepted";
      originClientMessageId: string; // accept したメッセージのID
    }
  | {
      messageType: "rejected";
      originClientMessageId: string; // reject したメッセージのID
      reason: string; // reject 理由; エラーコード的なものも付すべき？
    };
type WSLDSClientMessage =
  | (WSLDSMessageSheetIdentifiers & {
      messageType: "listen";
      listenerId: string;
      queries?: WSLDSQuery[]; // クエリ
      cacheHash?: string;
    })
  | (WSLDSMessageSheetIdentifiers & {
      messageType: "unlisten";
      listenerId: string;
      queries?: WSLDSQuery[]; // クエリ
    })
  | ({
      messageType: "write";
      subcommands: WSLDSSubCommand[];
    } & WSLDSMessageSheetIdentifiers)
  | {
      messageType: "refresh";
      jwt: string;
    };

const splitArrayCall = <T>(
  array: T[],
  chunkSize: number,
  mapCallback: (chunk: T[], offset: number) => void
) => {
  if (array.length < chunkSize) {
    mapCallback(array, 0);
  } else {
    // Split the array into chunks of chunkSize
    for (let offset = 0; offset < array.length; offset += chunkSize) {
      const chunk = array.slice(offset, offset + chunkSize);
      mapCallback(chunk, offset);
    }
  }
};

const computeListenCondHash = (
  sheetIdentifiers: WSLDSMessageSheetIdentifiers,
  queries: WSLDSQuery[] | null
) => {
  return computeObjectHash({ ...sheetIdentifiers, queries });
};

type DeferPromise<T> = {
  promise: Promise<T>;
  resolve: (value: T) => void;
  reject: (reason: any) => void;
};
const createDeferPromise = <T>(): DeferPromise<T> => {
  let resolve: (value: T) => void = () => {};
  let reject: (reason: any) => void = () => {};
  const promise = new Promise<T>((_resolve, _reject) => {
    resolve = _resolve;
    reject = _reject;
  });
  return { promise, resolve, reject };
};

class PromiseChain {
  _chain: Promise<void>;
  constructor() {
    this._chain = Promise.resolve();
  }
  add<T>(fn: () => Promise<T>) {
    return new Promise<T>((resolve, reject) => {
      this._chain = this._chain.then(
        () =>
          new Promise((finallyResolve) => {
            fn()
              .then((c) => {
                resolve(c);
              })
              .catch((e) => {
                reject(e);
              })
              .finally(finallyResolve);
          })
      );
    });
  }
}

type WSLDSMessageSource = "local" | "remote" | "channel";

type WSLDSChannelMessage =
  | {
      type: "ping";
    }
  | {
      type: "pong";
    }
  | {
      type: "wsmsg";
      data: WSLDSServerMessage;
    };

class WSLDSChannel {
  aliveOtherClient: boolean;
  ch: BroadcastChannel;
  onBroadcastServerMessage: (msg: WSLDSServerMessage) => void;
  constructor(onBroadcastServerMessage: (msg: WSLDSServerMessage) => void) {
    this.aliveOtherClient = false;
    this.onBroadcastServerMessage = onBroadcastServerMessage;
    this.ch = new BroadcastChannel("wslds_ch");
    this.ch.addEventListener("message", (e) => {
      this.onMessage(e.data);
    });
    this.ch.postMessage({ type: "pong" });
    this.onInterval();
    setInterval(
      () => {
        this.onInterval();
      },
      1000 * 60 * 1
    );
  }
  onInterval() {
    this.aliveOtherClient = false;
    this.ch.postMessage({ type: "ping" });
  }
  onMessage(msg: WSLDSChannelMessage) {
    if (msg.type === "wsmsg") {
      this.onBroadcastServerMessage(msg.data);
    } else if (msg.type === "ping") {
      this.ch.postMessage({ type: "pong" });
    } else if (msg.type === "pong") {
      this.aliveOtherClient = true;
    }
  }
  postMessage(msg: WSLDSServerMessage) {
    if (!this.aliveOtherClient) return;
    logger.log("pre-send message to other client");
    this.ch.postMessage({ type: "wsmsg", data: msg });
  }
}
class ReceivedServerMessageChecker {
  receivedIdsSet: Set<string>;
  receivedIdsArray: string[];
  maxLength: number;
  constructor() {
    this.maxLength = 100;
    this.receivedIdsSet = new Set();
    this.receivedIdsArray = [];
  }
  isReceived(id: string) {
    if (this.receivedIdsSet.has(id)) return true;
    this.receivedIdsSet.add(id);
    this.receivedIdsArray.push(id);
    if (this.receivedIdsArray.length > this.maxLength) {
      this.receivedIdsSet.delete(this.receivedIdsArray.shift()!);
    }
    return false;
  }
}

class WSLDS {
  jwtPromise: Promise<string>;
  resolveFirstJwt: ((value: string) => void) | null;
  ws: WebSocket | null;
  readyState: WSLDSReadyState;
  waitingQueue: Map<
    string,
    { resolve: (value: void) => void; reject: (reason: Error) => void }
  >;
  sheetListeners: Map<
    string,
    ((
      data: WSLDSServerMessage & {
        messageType: "allData" | "useCache" | "write";
      },
      ws: WebSocket
    ) => void)[]
  >;
  queryListeners: Map<
    string,
    {
      onMessage: (
        data: WSLDSServerMessage & {
          messageType: "allData" | "useCache" | "write";
        },
        ws: WebSocket
      ) => void;
      listenMessagePromise: Promise<WSLDSClientMessage>;
    }
  >;
  queuedGeneralMessages: {
    msg: WSLDSClientMessage;
    sendOnly: boolean;
    reusePromise: DeferPromise<void>;
  }[];
  queuedWriteMessages: {
    msg: WSLDSClientMessage;
    sendOnly: boolean;
    reusePromise: DeferPromise<void>;
    listenerId: string;
  }[];
  transactionChain: PromiseChain;
  manuallyCloseTimeout: number;
  reconnectTimeout: number;
  reconnectCount: number;
  prevReconnect: number;
  refreshTimeout: number;
  refreshChain: PromiseChain;
  wsldsChannel: WSLDSChannel;
  receivedServerMessageChecker: ReceivedServerMessageChecker;
  listeningSheetsSet: Set<string>;
  disposed: boolean;
  constructor() {
    this.readyState = WSLDSReadyState.CLOSED;
    this.manuallyCloseTimeout = 0;
    this.reconnectTimeout = 0;
    this.reconnectCount = 0;
    this.refreshTimeout = 0;
    this.prevReconnect = 0;
    this.refreshChain = new PromiseChain();
    this.ws = null;
    this._onOpen = this._onOpen.bind(this);
    this._onMessage = this._onMessage.bind(this);
    this._onMessageData = this._onMessageData.bind(this);
    this._onClose = this._onClose.bind(this);
    this._refresh = this._refresh.bind(this);
    this.waitingQueue = new Map();
    this.sheetListeners = new Map();
    this.queryListeners = new Map();
    this.queuedGeneralMessages = [];
    this.queuedWriteMessages = [];
    this.transactionChain = new PromiseChain();
    this.disposed = false;
    this.resolveFirstJwt = null;
    this.jwtPromise = new Promise((r) => {
      this.resolveFirstJwt = r;
    });
    if (auth.currentUser) {
      this._refresh();
    }
    onAuthStateChanged(auth, (user) => {
      this._refresh();
    });
    this.connect();
    this.wsldsChannel = new WSLDSChannel((msg) => {
      logger.log("recv pre-send message");
      this._onMessageData(msg, "channel");
    });
    this.receivedServerMessageChecker = new ReceivedServerMessageChecker();
    this.listeningSheetsSet = new Set();
  }
  async _onOpen() {
    if (this.disposed) return;
    this.reconnectCount = 0;
    this.readyState = WSLDSReadyState.OPEN;
    logger.log("onOpen");
    const T55M = 1000 * 60 * 55;
    const T4M = 1000 * 60 * 4;
    this.manuallyCloseTimeout = window.setTimeout(
      () => {
        if (this.ws) {
          this.ws.close();
          this.ws = null;
        }
      },
      Math.floor(T55M + Math.random() * T4M)
    );
    await Promise.all(
      [...this.queryListeners.values()].map((t) =>
        t.listenMessagePromise.then((listenMessage) =>
          this._send(listenMessage)
        )
      )
    );
    const processQueuedMessages = this.queuedGeneralMessages;
    this.queuedGeneralMessages = [];
    for (const { msg, sendOnly, reusePromise } of processQueuedMessages) {
      try {
        await this._send(msg, {
          sendOnly,
          queueIfFailed: true,
          reusePromise,
        });
      } catch (e) {
        // ignore
      }
    }
  }
  _onMessage(event: MessageEvent) {
    if (this.disposed) return;
    const data = JSON.parse(event.data) as WSLDSServerMessage;
    logger.log(`onMessage #{${data.messageType}}`, data);
    this._onMessageData(data, "local");
  }
  _onMessageData(data: WSLDSServerMessage, source: WSLDSMessageSource) {
    if (data.messageType === "expires") {
      //console.log(new Date(data.expiresAt));
    } else if (
      data.messageType === "allData" ||
      data.messageType === "useCache"
    ) {
      this.queryListeners.get(data.listenerId)?.onMessage(data, this.ws!);
    } else if (data.messageType === "write") {
      if (this.receivedServerMessageChecker.isReceived(data.clientMessageId)) {
        logger.log("skip received pre-send message");
      } else {
        if (source !== "channel") {
          this.wsldsChannel.postMessage(data);
        }
      }
      const sheetCond = {
        orgId: data.orgId,
        versionId: data.versionId,
        sheetId: data.sheetId,
      };
      const sheetListenKey = computeListenCondHash(sheetCond, null).toString(
        36
      );
      this.sheetListeners
        .get(sheetListenKey)
        ?.forEach((f) => f(data, this.ws!));
    } else if (data.messageType === "accepted") {
      const waitingQueue = this.waitingQueue.get(data.originClientMessageId);
      if (!waitingQueue) return;
      this.waitingQueue.delete(data.originClientMessageId);
      waitingQueue.resolve();
    } else if (data.messageType === "rejected") {
      const waitingQueue = this.waitingQueue.get(data.originClientMessageId);
      if (!waitingQueue) return;
      this.waitingQueue.delete(data.originClientMessageId);
      waitingQueue.reject(new Error(data.reason));
    } else {
      logger.error("unknown message type: " + (data as any)?.messageType);
    }
  }
  _onClose() {
    if (this.disposed) return;
    const prevReadyState = this.readyState;
    clearTimeout(this.manuallyCloseTimeout);
    clearTimeout(this.reconnectTimeout);
    this.reconnectCount++;
    logger.warn(`reconnecting... (${this.reconnectCount})`);
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.close();
    }
    this.ws = null;
    this.readyState = WSLDSReadyState.CLOSED;
    this.reconnectTimeout = window.setTimeout(
      () => {
        this.connect();
      },
      prevReadyState === WSLDSReadyState.OPEN
        ? Math.max(this.prevReconnect - Date.now() + 2000, 0)
        : 1000 + Math.floor(Math.random() * 1000)
    );
    this.prevReconnect = Date.now();
    this.listeningSheetsSet.clear();
  }
  _send(
    msg: WSLDSClientMessage,
    {
      sendOnly = false,
      queueIfFailed = false,
      reusePromise = null,
      listenerId = null,
    }: {
      sendOnly?: boolean;
      queueIfFailed?: boolean;
      reusePromise?: null | DeferPromise<void>;
      listenerId?: string | null;
    } = {}
  ) {
    const action = (
      reusePromise: DeferPromise<void>,
      tryDeferPromise?: DeferPromise<void>
    ) => {
      const { promise, resolve, reject } = reusePromise;
      logger.log(`send #{${msg.messageType}}`, msg);
      const clientMessageId = nanoid();
      const fullMsg = {
        clientMessageId,
        publishedAt: Date.now(),
        ...msg,
      };
      if (msg.messageType === "write")
        this._onMessageData(fullMsg as any, "local");
      try {
        if (
          this.readyState !== WSLDSReadyState.OPEN ||
          this.ws?.readyState !== WebSocket.OPEN
        ) {
          throw new Error("not connected");
        }
        if (msg.messageType === "write") {
          const sheetIdentifiers: WSLDSMessageSheetIdentifiers = {
            orgId: msg.orgId,
            versionId: msg.versionId,
            sheetId: msg.sheetId,
          };
          const sheetListenKey = computeListenCondHash(
            sheetIdentifiers,
            null
          ).toString(36);
          if (!this.listeningSheetsSet.has(sheetListenKey)) {
            throw new Error("not listening sheet");
          }
        }
        this.ws!.send(JSON.stringify(fullMsg));
        if (sendOnly) {
          resolve();
        } else {
          this.waitingQueue.set(clientMessageId, { resolve, reject });
        }
      } catch (e) {
        if (queueIfFailed) {
          logger.warn(e);
          logger.warn("not connected, added to queue.");
          if (msg.messageType === "write" && listenerId) {
            this.queuedWriteMessages.push({
              msg,
              sendOnly,
              reusePromise,
              listenerId,
            });
          } else {
            this.queuedGeneralMessages.push({ msg, sendOnly, reusePromise });
          }
        } else {
          reject(e);
          return;
        }
      } finally {
        tryDeferPromise?.resolve();
      }
    };
    if (reusePromise) {
      const tryDeferPromise = createDeferPromise<void>();
      action(reusePromise, tryDeferPromise);
      return tryDeferPromise.promise;
    }
    const newDeferPromise = createDeferPromise<void>();
    action(newDeferPromise);
    return newDeferPromise.promise;
  }
  _refresh() {
    if (this.disposed) return;
    this.refreshChain.add(async () => {
      clearTimeout(this.refreshTimeout);
      try {
        const jwt = await auth.currentUser!.getIdToken(true);
        this._send(
          {
            messageType: "refresh",
            jwt,
          },
          { sendOnly: true }
        ).catch((e) => {
          // ignore;
        });
        if (this.resolveFirstJwt) {
          this.resolveFirstJwt(jwt);
          this.resolveFirstJwt = null;
        } else {
          this.jwtPromise = Promise.resolve(jwt);
        }
        this.refreshTimeout = window.setTimeout(this._refresh, 1000 * 60 * 30);
      } catch (e) {
        console.warn(e);
        this.refreshTimeout = window.setTimeout(this._refresh, 1000 * 1);
      }
    });
  }
  async connect() {
    if (this.disposed) return;
    if (this.readyState !== WSLDSReadyState.CLOSED) return;
    clearTimeout(this.reconnectTimeout);
    this.readyState = WSLDSReadyState.CONNECTING;
    try {
      const jwt = await this.jwtPromise;
      this.ws = new WebSocket(
        `${API_CONFIG.baseURL.replace(/^http/, "ws").replace(/\/v1/, "")}/beta/lds/ws?jwt=${jwt}`
      );
      this.ws.onopen = this._onOpen;
      this.ws.onmessage = this._onMessage;
      this.ws.onclose = this._onClose;
      this.ws.onerror = (e) => {
        console.error(e);
        this._onClose();
      };
      //@ts-ignore
      window.ws = this.ws;
    } catch (e) {
      console.error(e);
      this._onClose();
    }
  }
  listen(
    sheetIdentifiers: WSLDSMessageSheetIdentifiers,
    queries: WSLDSQuery[] | null,
    onUpdated: (
      rows: WSLDSSheetDataRow[],
      rowIndexes: {
        [key: string]: number;
      }
    ) => void
  ) {
    let listenDisposed = false;
    if (queries?.length === 0) queries = null;
    /*queries = [
      {
        dataType: "string",
        operator: "==",
        schema: "pQHscuOHqwDugpztcdBD",
        targetValue: "うどん",
      },
    ];*/
    const sheetListenKey = computeListenCondHash(
      sheetIdentifiers,
      null
    ).toString(36);
    const queryListenKey = computeListenCondHash(
      sheetIdentifiers,
      queries
    ).toString(36);
    let rows: WSLDSSheetDataRow[] = [];
    let rowIndexes: { [key: string]: number } = {};
    const deleteRows = (rowIds: string[]) => {
      let firstDeletedIndex = -1;
      const deleteRowIdsSet = new Set(rowIds);
      for (let i = 0; i < rows.length; i++) {
        if (deleteRowIdsSet.has(rows[i].id)) {
          if (firstDeletedIndex < 0) firstDeletedIndex = i;
          let deleteConsecutiveCount = 0;
          const fromIdx = i;
          for (
            ;
            i + deleteConsecutiveCount < rows.length &&
            deleteRowIdsSet.has(rows[i + deleteConsecutiveCount].id);
            deleteConsecutiveCount++
          );
          rows.splice(fromIdx, deleteConsecutiveCount);
          i--;
        }
      }
      for (const rowId of rowIds) {
        delete rowIndexes[rowId];
      }
      if (firstDeletedIndex !== -1) {
        for (let i = firstDeletedIndex; i < rows.length; i++) {
          rowIndexes[rows[i].id] = i;
        }
      }
    };
    const processWriteMsg = (
      msg: WSLDSServerMessage & { messageType: "write" }
    ) => {
      if (receivedServerMessageChecker.isReceived(msg.clientMessageId)) {
        logger.log("skip received pre-send message w/query");
        return;
      }
      rows = [...rows];
      for (const subcommand of msg.subcommands) {
        if (subcommand.subcommandType === "insert") {
          let insertRows = subcommand.rows;
          if (queries?.length) {
            insertRows = insertRows.filter(getFilterRowsByQueriesFn(queries));
          }
          const afterRowId = subcommand.afterRowId;
          const insertIndex =
            (afterRowId === undefined
              ? -1
              : rowIndexes[afterRowId] ?? rows.length - 1) + 1;
          //JavaScriptにも限界はある(65535以上の引数は渡せない)
          splitArrayCall(insertRows, 50000, (insertRowsChunk, offset) => {
            rows.splice(insertIndex + offset, 0, ...insertRowsChunk);
          });
          for (let i = insertIndex; i < rows.length; i++) {
            rowIndexes[rows[i].id] = i;
          }
        } else if (subcommand.subcommandType === "update") {
          for (const row of subcommand.rows) {
            const targetRowIndex = rowIndexes[row.id];
            if (targetRowIndex !== undefined) {
              rows[targetRowIndex] = row;
            } else {
              rows.push(row);
              rowIndexes[row.id] = rows.length - 1;
            }
          }
          if (queries?.length) {
            const filterFn = getFilterRowsByQueriesFn(queries);
            const unmatchedRowIds = subcommand.rows
              .filter((row) => !filterFn(row))
              .map((row) => row.id);
            deleteRows(unmatchedRowIds);
          }
        } else if (subcommand.subcommandType === "delete") {
          deleteRows(subcommand.rowIds);
        } else if (subcommand.subcommandType === "move") {
          let targetRowIds = subcommand.rowIds;
          const afterRowId = subcommand.afterRowId;
          let skipMove = false;
          if (queries?.length) {
            targetRowIds = targetRowIds.filter((id) =>
              Object.hasOwn(rowIndexes, id)
            );
          }
          if (afterRowId && rowIndexes[afterRowId] === undefined) {
            // 移動先が分からない移動は無視する
            skipMove = true;
          }
          if (!skipMove) {
            const targetRowIdsSet = new Set(targetRowIds);
            const newRows: WSLDSSheetDataRow[] = [];
            const addTargetRows = () => {
              //JavaScriptにも限界はある(65535以上の引数は渡せない)
              splitArrayCall(targetRowIds, 50000, (targetRowIdsChunk) => {
                newRows.push(
                  ...targetRowIdsChunk.map((id) => rows[rowIndexes[id]])
                );
              });
            };
            let insertIndex = 0;
            if (!afterRowId) {
              addTargetRows();
            }
            for (const row of rows) {
              if (targetRowIdsSet.has(row.id)) {
                // skip
                if (insertIndex > newRows.length) {
                  insertIndex = newRows.length;
                }
              } else {
                newRows.push(row);
                if (row.id === afterRowId) {
                  if (insertIndex > newRows.length) {
                    insertIndex = newRows.length;
                  }
                  addTargetRows();
                }
              }
            }
            rows = newRows;
            for (let i = insertIndex; i < rows.length; i++) {
              rowIndexes[rows[i].id] = i;
            }
          }
        }
      }
    };
    //let waitingFirstRecvMsgs: WSLDSServerMessage[] = [];
    const firstRecived = new WeakSet<WebSocket>();
    let cached: {
      cacheHash: string;
      rows: WSLDSSheetDataRow[];
    } | null = null;
    const updateCache = (
      _cached: {
        cacheHash: string;
        rows: WSLDSSheetDataRow[];
      } | null
    ) => {
      if (_cached) cached = _cached;
      ldsIndexedDBCache.setThrottled(queryListenKey, _cached);
    };
    const receivedServerMessageChecker = new ReceivedServerMessageChecker();
    const onMessage = async (
      msg: WSLDSServerMessage & {
        messageType: "allData" | "useCache" | "write";
      },
      ws: WebSocket
    ) => {
      if (listenDisposed) return;
      if (msg.messageType === "allData" || msg.messageType === "useCache") {
        if (msg.messageType === "allData") {
          rows = msg.rows || [];
          updateCache({
            cacheHash: msg.cacheHash || "0".repeat(16),
            rows,
          });
        } else {
          rows = cached?.rows || [];
          updateCache(null);
        }
        rowIndexes = Object.create(null);
        for (let i = 0; i < rows.length; i++) {
          rowIndexes[rows[i].id] = i;
        }
        if (ws && !firstRecived.has(ws)) {
          firstRecived.add(ws);
          /*for (const msg of waitingFirstRecvMsgs) {
            processWriteMsg(msg);
          }
          waitingFirstRecvMsgs = [];*/
        }
        const processQueuedMessages = this.queuedWriteMessages.filter(
          (c) => c.listenerId === sheetListenKey
        );
        this.queuedWriteMessages = this.queuedWriteMessages.filter(
          (c) => c.listenerId !== sheetListenKey
        );
        this.listeningSheetsSet.add(sheetListenKey);
        for (const { msg, sendOnly, reusePromise } of processQueuedMessages) {
          try {
            await this._send(msg, {
              sendOnly,
              queueIfFailed: true,
              reusePromise,
              listenerId: sheetListenKey,
            });
          } catch (e) {
            // ignore
          }
        }
        onUpdated(rows, rowIndexes);
      } else if (msg.messageType === "write") {
        if (ws && !firstRecived.has(ws)) {
          //waitingFirstRecvMsgs.push(msg);
          return;
        }
        processWriteMsg(msg);
        if (queries === null) {
          const nPrevHash = parseInt(cached?.cacheHash ?? "0", 16);
          const nNewHash = cyrb53(msg.clientMessageId);
          const nMergedHash = xor53(nPrevHash, nNewHash);
          const hexMergedHash = nMergedHash.toString(16).padStart(16, "0");
          updateCache({
            cacheHash: hexMergedHash,
            rows,
          });
        }
        onUpdated(rows, rowIndexes);
      }
    };
    this.sheetListeners.set(
      sheetListenKey,
      this.sheetListeners.get(sheetListenKey)?.concat(onMessage) || [onMessage]
    );
    const listenMessagePromise: Promise<WSLDSClientMessage> = ldsIndexedDBCache
      .get(queryListenKey)
      .then((_cached) => {
        cached = _cached;
        return {
          messageType: "listen",
          listenerId: queryListenKey,
          ...sheetIdentifiers,
          ...(queries ? { queries } : {}),
          ...(cached?.cacheHash ? { cacheHash: cached.cacheHash } : {}),
        };
      });
    this.queryListeners.set(queryListenKey, {
      onMessage,
      listenMessagePromise,
    });
    listenMessagePromise.then((listenMessage) => {
      this._send(listenMessage).catch((e) => {
        //ignore
      });
    });
    return () => {
      listenDisposed = true;
      this.sheetListeners.set(
        sheetListenKey,
        this.sheetListeners.get(sheetListenKey)!.filter((f) => f !== onMessage)
      );
      this.queryListeners.delete(queryListenKey);
      this._send({
        messageType: "unlisten",
        listenerId: queryListenKey,
        ...sheetIdentifiers,
      }).catch(() => {
        // ignore
      });
      this.listeningSheetsSet.delete(sheetListenKey);
    };
  }
  async runTransaction(
    sheetIdentifiers: WSLDSMessageSheetIdentifiers,
    queries: WSLDSQuery[] | null,
    subcommands: WSLDSSubCommand[]
  ) {
    const sheetListenKey = computeListenCondHash(
      sheetIdentifiers,
      null
    ).toString(36);
    const queryListenKey = computeListenCondHash(
      sheetIdentifiers,
      queries
    ).toString(36);
    await this._send(
      {
        messageType: "write",
        subcommands,
        ...sheetIdentifiers,
      },
      {
        queueIfFailed: true,
        listenerId: sheetListenKey,
      }
    );
  }
  dispose() {
    this.disposed = true;
    clearTimeout(this.reconnectTimeout);
    if (this.ws) this.ws.close();
  }
}

class WSLDSListenPool {
  wslds: WSLDS;
  pool: {
    [key: string]: {
      deadTimeout: number;
      lastMessage: Parameters<Parameters<WSLDS["listen"]>[2]> | null;
      listeners: {
        listenSID: string;
        onUpdated: Parameters<WSLDS["listen"]>[2];
      }[];
      unlisten: () => void;
    };
  };
  constructor(wslds: WSLDS) {
    this.wslds = wslds;
    this.pool = Object.create(null);
  }
  generateListenGroup(
    sheetIdentifiers: WSLDSMessageSheetIdentifiers,
    queries: WSLDSQuery[] | null
  ) {
    const listeners: {
      listenSID: string;
      onUpdated: Parameters<WSLDS["listen"]>[2];
    }[] = [];
    const listenGroup = {
      lastMessage: null as Parameters<Parameters<WSLDS["listen"]>[2]> | null,
      deadTimeout: 0,
      listeners,
      unlisten: this.wslds.listen(sheetIdentifiers, queries, (...args) => {
        listenGroup.lastMessage = args;
        for (const { onUpdated } of listeners) {
          onUpdated(...args);
        }
      }),
    };
    return listenGroup;
  }
  listen(
    sheetIdentifiers: WSLDSMessageSheetIdentifiers,
    queries: WSLDSQuery[] | null,
    onUpdated: Parameters<WSLDS["listen"]>[2]
  ) {
    const listenSID = Math.random().toString(36).slice(2);
    const id = JSON.stringify(sheetIdentifiers) + JSON.stringify(queries);
    if (!this.pool[id]) {
      this.pool[id] = this.generateListenGroup(sheetIdentifiers, queries);
    }
    const listenGroup = this.pool[id];
    clearTimeout(listenGroup.deadTimeout);
    listenGroup.listeners.push({
      listenSID,
      onUpdated,
    });
    const lastMessage = listenGroup.lastMessage;
    if (lastMessage !== null) {
      setTimeout(() => {
        onUpdated(...lastMessage);
      }, 0);
    }
    return () => {
      listenGroup.listeners.splice(
        listenGroup.listeners.findIndex((l) => l.listenSID === listenSID),
        1
      );
      if (listenGroup.listeners.length === 0) {
        listenGroup.deadTimeout = window.setTimeout(() => {
          listenGroup.unlisten();
          delete this.pool[id];
        }, 1000 * 20);
      }
    };
  }
}

class SheetUpdatedAtManager {
  notRequiredUntilTimeMap: { [key: string]: number };
  constructor() {
    this.notRequiredUntilTimeMap = Object.create(null);
  }
  isRequireUpdate(identifier: string) {
    const now = Date.now();
    const notRequiredUntilTime = this.notRequiredUntilTimeMap[identifier] ?? 0;
    if (notRequiredUntilTime > now) {
      return false;
    }
    this.notRequiredUntilTimeMap[identifier] = now + 1000 * 60;
    return true;
  }
}

let wslds = new WSLDS();
const wsldsListenPool = new WSLDSListenPool(wslds);
const sheetUpdatedAtManager = new SheetUpdatedAtManager();

export const SheetRowsIOWSLDSHost = memo(
  ({
    sheetFieldSchemas,
    organizationId,
    versionId,
    sheetId,
    queries,
    setSheetRowsIOUnique,
    setRawRowsState,
  }: {
    sheetFieldSchemas: SheetFieldSchema[];
    organizationId: string;
    versionId: string;
    sheetId: string;
    queries: SheetRowsIOQuery[] | null;
    setSheetRowsIOUnique: (
      value: Pick<SheetRowsIO<unknown>, "runTransaction" | "getRowById"> | null
    ) => void;
    setRawRowsState: React.Dispatch<
      React.SetStateAction<{
        rawRows: {
          id: string;
          value: MinimalTypeSchemasToRawSheetRowData<unknown>;
        }[];
        rowIndexes: { [key: string]: number };
        isRowsLoaded: boolean;
      }>
    >;
  }) => {
    const getNormalizedRow = useMemo(() => {
      const cacheWeakMap = new WeakMap<
        WSLDSSheetDataRow,
        { id: string; value: { [key: string]: string } }
      >();
      return (wsldsRow: WSLDSSheetDataRow) => {
        const cachedRow = cacheWeakMap.get(wsldsRow);
        if (cachedRow) return cachedRow;
        const value = {} as { [key: string]: string };
        for (const schema of sheetFieldSchemas) {
          value[schema.name] = wsldsRow.value[schema.id] ?? "";
        }
        const row = { id: wsldsRow.id, value };
        cacheWeakMap.set(wsldsRow, row);
        return row;
      };
    }, [sheetFieldSchemas]);
    const getDBRow = useMemo(() => {
      return (normalizedRow: {
        id: string;
        value: { [key: string]: string };
      }) => {
        const value = {} as { [key: string]: string };
        for (const schema of sheetFieldSchemas) {
          value[schema.id] = normalizedRow.value[schema.name] ?? "";
        }
        return { id: normalizedRow.id, value };
      };
    }, [sheetFieldSchemas]);
    const [sheet] = useSheet(sheetId, { organizationId, versionId });
    const refSheet = useRef<Sheet | null>(sheet);
    refSheet.current = sheet;
    const updateSheet = useUpdateSheet();
    wslds = wslds || new WSLDS();
    //@ts-ignore
    window.wslds = wslds;
    useEffect(() => {
      if (!sheet) return;
      const sheetUid = organizationId + versionId + sheetId;
      const runTransaction = async (
        jobs: SheetRowsIOTransactionJob<unknown>[],
        {
          historyMode = false,
        }: {
          historyMode?: boolean;
        } = {}
      ): Promise<void> => {
        if (!jobs.length) return;
        if (sheetUpdatedAtManager.isRequireUpdate(sheetUid)) {
          if (!refSheet.current) return;
          updateSheet(
            {
              ...refSheet.current!,
              updatedAt: new Date().toISOString(),
              lastTransactionId: generateId(),
            },
            { organizationId, versionId, sheetId }
          );
        }
        const subcommands: WSLDSSubCommand[] = jobs.map((job) => {
          if (job.type === "insert") {
            const nanoidStream = genNanoidStream(job.rows.length);
            return {
              subcommandType: "insert",
              rows: job.rows.map((row) =>
                getDBRow({
                  id: row.id ?? nanoidStream(),
                  value: row.value,
                })
              ),
              afterRowId: job.insertAfterRowId,
            };
          } else if (job.type === "update") {
            return {
              subcommandType: "update",
              rows: job.rows.map(getDBRow),
            };
          } else if (job.type === "delete") {
            return {
              subcommandType: "delete",
              rowIds: job.rowIds,
            };
          } else if (job.type === "move") {
            return {
              subcommandType: "move",
              rowIds: job.rowIds,
              afterRowId: job.moveAfterRowId,
            };
          }
          throw new Error("invalid job type");
        });
        const transactionPromise = wslds.runTransaction(
          { orgId: organizationId, versionId, sheetId },
          null,
          subcommands
        );
        if (!historyMode) {
          await transactionPromise;
        }
      };
      let rows = [] as WSLDSSheetDataRow[];
      let rowIndexes = {} as { [key: string]: number };
      const getRowById = (id: string) => rows[rowIndexes[id]];
      setSheetRowsIOUnique({
        runTransaction,
        getRowById,
      });
      const dispose = wsldsListenPool.listen(
        { orgId: organizationId, versionId, sheetId },
        queries?.map(({ sheetFieldSchemaId, operator, value }) => {
          const fieldType =
            sheetFieldSchemas.find((s) => s.id === sheetFieldSchemaId)?.type ||
            "string";
          return {
            dataType:
              fieldType === "integer" || fieldType === "float"
                ? "number"
                : "string",
            operator: operator as WSLDSQueryOperator,
            schema: sheetFieldSchemaId,
            targetValue: value,
          };
        }) ?? null,
        (newRows, newRowIndexes) => {
          const newRowsNormalized = newRows.map(getNormalizedRow);
          rowIndexes = newRowIndexes;
          rows = newRowsNormalized;
          setRawRowsState({
            rawRows: newRowsNormalized,
            rowIndexes,
            isRowsLoaded: true,
          });
        }
      );
      return () => {
        setRawRowsState({ rawRows: [], rowIndexes: {}, isRowsLoaded: false });
        setSheetRowsIOUnique(null);
        dispose();
      };
    }, [
      sheetFieldSchemas,
      organizationId,
      versionId,
      sheetId,
      !!sheet,
      queries,
    ]);
    return null;
  }
);
